import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Link,
  Select,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { Add, Remove } from "@mui/icons-material";
import Switch from "@mui/material/Switch";

import SearchBar from "../../components/search/Search";
import Table, {
  type TableColumn,
} from "../../components/CommonTable/CommonTable";
import { StudyMaterialCartRead } from "../../../Redux/Slices/Booklet/Tutor/StudyMaterial/StudyMaterialCartRead";
import { useAppDispatch, useAppSelector } from "../../../Redux/Store/Hooks";
import { studyMaterialRequestCreate } from "../../../Redux/Slices/Booklet/Tutor/StudyMaterial/StudyMaterialRequestCreate";
import { studyMaterialAddToCartCreate } from "../../../Redux/Slices/Booklet/Tutor/StudyMaterial/StudyMaterialAddToCartCreate";
import { PrinterOptions } from "../../../Redux/Slices/Booklet/Tutor/PrinterOptions/PrinterOptions";
import ConfirmDialog from "../../components/ComformDialog/ConformDialog";
import { DefaultPrinter } from "../../../Redux/Slices/Booklet/Tutor/PrinterOptions/DefaultPrinter";
import { CartItem } from "./ICartPage";
import { DashboardPrintlist } from "../../../Redux/Slices/Booklet/Tutor/PrinterOptions/DashboardPrintlist";
import { DefaultPrinterUpdate } from "../../../Redux/Slices/Booklet/Tutor/PrinterOptions/DefaultPrinterChange";

interface FileDetails {
  id: string;
  name: string;
  year_group: string;
  mimeType: string;
  webViewLink: string;
  modifiedTime: string;
  parents: string[];
}

interface PrintForm {
  paperSize: string;
  side: string;
  staplePosition: string | null;
  color: string;
  orientation: string;
  scale: string;
  stapler: string;
  pages_per_sheet: string;
  remark: string;
  center_id?: string;
  printer_id?: string;
}

const CartPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { data: cartItems, isLoading: cartLoading } = useAppSelector(
    (state) => state.StudyMaterialCartReadSlice
  );

  const { data: printeroptions } = useAppSelector(
    (state) => state.PrinterOptionsSlice
  );

  const { data: PrinterList } = useAppSelector(
    (state) => state.DashbnoardPrintList
  );

  const { data: Defaultprinteroptions } = useAppSelector(
    (state) => state.DefaultPrinterOptionsUpdate
  );

  // console.log("Default Printer Data:", Defaultprinteroptions);

  const [localCartItems, setLocalCartItems] = useState<CartItem[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [editPrinterOptions, setEditPrinterOptions] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, boolean>>({});
  const [selectedPrinter, setSelectedPrinter] = useState<string>("");
  const [selectedCenter, setSelectedCenter] = useState<string>("");
  const [selectedPrinterError, setSelectedPrinterError] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const hasDefaultPrinter =
    Defaultprinteroptions?.status !== false &&
    Defaultprinteroptions?.status_code !== 404;

  useEffect(() => {
    if (cartItems?.data && Array.isArray(cartItems.data)) {
      setLocalCartItems(cartItems.data);

      const firstItem = cartItems.data[0];
      if (firstItem?.center?.center_id) {
        setSelectedCenter(firstItem.center.center_id);
      }
    }
  }, [cartItems]);

  // Fetch cart on mount
  useEffect(() => {
    dispatch(StudyMaterialCartRead());
    dispatch(PrinterOptions());
  }, [dispatch]);

  useEffect(() => {
    if (selectedCenter) {
      dispatch(DashboardPrintlist({ center_id: selectedCenter }));
    }
  }, [cartItems, selectedCenter]);

  useEffect(() => {
    if (selectedPrinter) {
      dispatch(DefaultPrinterUpdate({ id: selectedPrinter }));
    }
  }, [selectedPrinter]);

  const getDateChipProps = (dateString: string) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return { label: "Invalid Date", color: "error" as const };
      }

      const now = new Date();
      const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

      if (diffInSeconds < 0) {
        return { label: "In the future", color: "info" as const };
      }

      if (diffInSeconds < 60) {
        return { label: "Just now", color: "success" as const };
      }

      if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60);
        return {
          label: minutes === 1 ? "1 minute ago" : `${minutes} minutes ago`,
          color: "success" as const,
        };
      }

      if (diffInSeconds < 86400) {
        const hours = Math.floor(diffInSeconds / 3600);
        return {
          label: hours === 1 ? "1 hour ago" : `${hours} hours ago`,
          color: "success" as const,
        };
      }

      if (diffInSeconds < 604800) {
        const days = Math.floor(diffInSeconds / 86400);
        return {
          label: days === 1 ? "Yesterday" : `${days} days ago`,
          color: "warning" as const,
        };
      }

      if (diffInSeconds < 2592000) {
        const weeks = Math.floor(diffInSeconds / 604800);
        return {
          label: weeks === 1 ? "1 week ago" : `${weeks} weeks ago`,
          color: "warning" as const,
        };
      }

      if (diffInSeconds < 31536000) {
        const months = Math.floor(diffInSeconds / 2592000);
        return {
          label: months === 1 ? "1 month ago" : `${months} months ago`,
          color: "default" as const,
        };
      }

      const years = Math.floor(diffInSeconds / 31536000);
      return {
        label: years === 1 ? "1 year ago" : `${years} years ago`,
        color: "default" as const,
      };
    } catch (error) {
      console.error("Error parsing date:", dateString, error);
      return { label: "Invalid Date", color: "error" as const };
    }
  };

  const optionsByCategory = React.useMemo(() => {
    if (!printeroptions?.data) return {};
    return Array.isArray(printeroptions.data)
      ? printeroptions.data.reduce((acc: any, option: any) => {
          if (!acc[option.category_id]) acc[option.category_id] = [];
          acc[option.category_id].push(option);
          return acc;
        }, {})
      : {};
  }, [printeroptions]);

  const handleQuantityChange = async (
    row: CartItem,
    index: number,
    newQty: number
  ) => {
    const validQty = Math.max(0, newQty);

    setLocalCartItems((prev) => {
      const updated = prev.map((item) =>
        item.cart_id === row.cart_id ? { ...item, quantity: validQty } : item
      );
      return updated.filter((item) => item.quantity > 0);
    });

    const payload = {
      cart_id: row.cart_id,
      pdf_id: row.pdf_id,
      tutor_id: row.tutor_id,
      subject_id: row.subject?.id,
      center_id: row.center?.center_id,
      quantity: validQty,
      status: validQty === 0 ? false : row.status,
    };
    dispatch(studyMaterialAddToCartCreate(payload));

    // if (res?.payload?.message === "Cart updated successfully.") {
    //   dispatch(StudyMaterialCartRead());
    // }

    // if (validQty === 0 && res?.payload) {
    //   dispatch(StudyMaterialCartRead());
    // }
  };

  const [printForm, setPrintForm] = useState<PrintForm>({
    paperSize: "",
    side: "",
    staplePosition: null,
    color: "",
    orientation: "",
    scale: "",
    stapler: "YES",
    pages_per_sheet: "",
    remark: "",
    center_id: "",
    printer_id: "",
  });

  const handlePrintChange = (field: keyof PrintForm, value: string) => {
    setPrintForm((prev) => ({ ...prev, [field]: value }));
  };

  const isFormValid = () => {
    // remark must always be filled
    // if (!printForm.remark.trim()) {
    //   return false;
    // }

    if (!editPrinterOptions) {
      // when not using default printer, validate all fields
      if (
        !printForm.paperSize ||
        !printForm.side ||
        !printForm.color ||
        !printForm.orientation ||
        !printForm.scale
      ) {
        return false;
      }

      if (printForm.stapler === "YES" && !printForm.staplePosition) {
        return false;
      }

      if (!printForm.pages_per_sheet) {
        return false;
      }
    }

    // must have at least 1 item with quantity > 0
    if (!localCartItems.some((item) => item.quantity > 0)) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (Defaultprinteroptions?.data) {
      const def = Defaultprinteroptions.data;

      setPrintForm({
        paperSize: def.papper_size || "A4",
        side: def.side || "Double Side",
        staplePosition: def.stapler_position || null,
        color: def.color || "Black and White",
        orientation: def.page_orientation || "Portrait",
        scale: def.scale || "Fit to Width",
        stapler: def.stapler ? "YES" : "NO",
        pages_per_sheet: def.pages_per_sheet || "",
        remark: "",
      });
    }

    // ✅ If no printer assigned, force-disable default printer usage
    if (
      Defaultprinteroptions?.status === false &&
      Defaultprinteroptions?.status_code === 404
    ) {
      setEditPrinterOptions(false);
    }
  }, [Defaultprinteroptions]);

  // ⬇️ Reset to default printer settings when switching OFF edit mode
  useEffect(() => {
    if (!editPrinterOptions && Defaultprinteroptions?.data) {
      const def = Defaultprinteroptions.data;

      setPrintForm((prev) => ({
        ...prev,
        paperSize: def.papper_size || "A4",
        side: def.side || "Double Side",
        staplePosition: def.stapler_position || null,
        color: def.color || "Black and White",
        orientation: def.page_orientation || "Portrait",
        scale: def.scale || "Fit to Width",
        stapler: def.stapler ? "YES" : "NO",
        pages_per_sheet: def.pages_per_sheet || "",
        // Keep the existing remark
        remark: prev.remark,
      }));
    }
  }, [editPrinterOptions, Defaultprinteroptions]);

  const handleSubmit = async () => {
    const errors: Record<string, boolean> = {};

    // // ✅ Validate remark
    // if (!printForm.remark.trim()) {
    //   errors.remark = true;
    // }

    if (!selectedPrinter) {
      setSelectedPrinterError(true);
      return;
    }

    // ✅ Validate printer settings only when editing or no default printer
    if (!hasDefaultPrinter || editPrinterOptions) {
      const requiredFields: (keyof PrintForm)[] = [
        "paperSize",
        "side",
        "color",
        "orientation",
        "scale",
        "pages_per_sheet",
      ];

      requiredFields.forEach((field) => {
        if (!printForm[field] || printForm[field]?.toString().trim() === "") {
          errors[field] = true;
        }
      });

      if (printForm.stapler === "YES" && !printForm.staplePosition) {
        errors.staplePosition = true;
      }
    }

    // ✅ Validate quantity
    if (!localCartItems.some((item) => item.quantity > 0)) {
      setDialogMessage(
        "Please add at least one item with quantity greater than 0."
      );
      setOpenDialog(true);
      return;
    }

    // ✅ Update form errors
    setFormErrors(errors);

    // ✅ If there are any validation errors, DO NOT open dialog
    if (Object.keys(errors).length > 0) {
      return; // just stop here — don't open dialog
    }
    setSubmitting(true);
    // ✅ Proceed only when no validation errors
    let payload;
    if (!editPrinterOptions && Defaultprinteroptions?.data) {
      const def = Defaultprinteroptions.data;
      payload = {
        request_id: "0",
        status: true,
        tutor_remarks: printForm.remark,
        cart_ids: localCartItems.map((item) => item.cart_id),
        papper_size: def.papper_size,
        side: def.side,
        page_orientation: def.page_orientation,
        scale: def.scale,
        stapler: def.stapler,
        stapler_position: def.stapler_position,
        pages_per_sheet: def.pages_per_sheet,
        color: def.color,
        center_id: selectedCenter,
        printer_id: selectedPrinter,
      };
    } else {
      payload = {
        request_id: "0",
        status: true,
        tutor_remarks: printForm.remark || "",
        cart_ids: localCartItems.map((item) => item.cart_id),
        papper_size: printForm.paperSize,
        side: printForm.side,
        page_orientation: printForm.orientation,
        scale: printForm.scale,
        stapler: printForm.stapler === "YES",
        stapler_position: printForm.staplePosition || null,
        pages_per_sheet: printForm.pages_per_sheet,
        color: printForm.color,
        center_id: selectedCenter,
        printer_id: selectedPrinter,
      };
    }

    try {
      const response = await dispatch(studyMaterialRequestCreate(payload));
      const message =
        (response as any)?.payload?.message || "Request created successfully!";
      setDialogMessage(message);
    } catch (error: any) {
      setDialogMessage(error?.message || "Failed to create request");
    }
    setSubmitting(false);
    setOpenDialog(true);
  };

  const filteredCartItems = localCartItems.filter((item) => {
    if (!searchValue) return true;
    const searchLower = searchValue.toLowerCase();
    return (
      item.pdf_data?.name?.toLowerCase().includes(searchLower) ||
      item.pdf_id.toLowerCase().includes(searchLower)
    );
  });

  const columns: TableColumn<CartItem>[] = [
    // {
    //   key: "webViewLink",
    //   title: "Drive Link",
    //   render: (value, row) => {
    //     const link = row.pdf_data?.webViewLink;
    //     return link ? (
    //       <Link
    //         href={link}
    //         target="_blank"
    //         rel="noopener noreferrer"
    //         sx={{ color: "primary.main", textDecoration: "none" }}
    //       >
    //         Open Drive
    //       </Link>
    //     ) : (
    //       <Typography color="textSecondary">No Link</Typography>
    //     );
    //   },
    // },
    {
      key: "subject", // must match CartItem property
      title: "Subject",
      render: (value, row) => (
        <Typography sx={{ fontSize: "14px", fontWeight: "500" }}>
          {row.subject?.name || "Unknown"}
        </Typography>
      ),
    },
    {
      key: "year_group",
      title: "Year group",
      render: (value, row) => (
        <Typography sx={{ fontSize: "14px", fontWeight: "500" }}>
          {row.subject?.year_group?.year_group || "Unknown"}
        </Typography>
      ),
    },

    {
      key: "name",
      title: "File Name",
      render: (value, row) => (
        <Typography sx={{ fontSize: "14px", fontWeight: "500" }}>
          {row.pdf_data?.name || "Unknown"}
        </Typography>
      ),
    },
    {
      key: "preview",
      title: "Preview",
      render: () => <Typography color="textSecondary">Coming Soon</Typography>,
    },
    {
      key: "pdf_data.modifiedTime",
      title: "Last Updated",
      sortable: true,
      render: (_: unknown, row: any) => {
        const dateToFormat = row.pdf_data?.modifiedTime;
        if (!dateToFormat) {
          return <Chip label="Unknown" color="error" size="small" />;
        }

        const chipProps = getDateChipProps(dateToFormat);
        return <Chip {...chipProps} size="small" variant="outlined" />;
      },
    },

    {
      key: "quantity",
      title: "Request Qty",
      align: "center",
      render: (value, row, index) => (
        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
          <IconButton
            size="small"
            sx={{
              bgcolor: "#009DFF",
              color: "white",
              "&:hover": { bgcolor: "#007ACC" },
            }}
            onClick={() => handleQuantityChange(row, index, row.quantity - 1)}
            aria-label="Decrease quantity"
          >
            <Remove fontSize="small" />
          </IconButton>

          <TextField
            type="number"
            value={row.quantity}
            onChange={(e) => {
              const newQty = parseInt(e.target.value, 10);
              if (!isNaN(newQty) && newQty >= 0) {
                handleQuantityChange(row, index, newQty);
              }
            }}
            inputProps={{ min: 0, style: { textAlign: "center" } }}
            size="small"
            sx={{ width: "70px" }}
          />

          <IconButton
            size="small"
            sx={{
              bgcolor: "#009DFF",
              color: "white",
              "&:hover": { bgcolor: "#007ACC" },
            }}
            onClick={() => handleQuantityChange(row, index, row.quantity + 1)}
            aria-label="Increase quantity"
          >
            <Add fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: { xs: "15px", md: "25px" }, // smaller gap on mobile
        p: { xs: 1, sm: 2, md: 3 }, // padding adjusts per screen
      }}
    >
      {/* Search */}
      {/* <Box>
        <SearchBar
          value={searchValue}
          onChange={setSearchValue}
          placeholder="Search files..."
          onSubmit={() => console.log("Search submitted:", searchValue)}
        />
      </Box> */}

      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap={{ xs: 0.5, sm: 1 }}
        flexWrap="wrap"
      >
        <Table<CartItem>
          columns={columns}
          data={filteredCartItems}
          loading={cartLoading}
          onQuantityChange={handleQuantityChange}
          getPreviewId={(row) => row.pdf_data?.id || row.pdf_id}
          pageType="cart"
        />
      </Box>

      {/* Back link */}
      <Box>
        <Typography
          onClick={() => navigate("/study_materials")}
          sx={{
            cursor: "pointer",
            color: "primary.main",
            textAlign: "right",
            fontSize: "14px",
            fontWeight: "500",
            marginRight: "10px",
          }}
        >
          Back to Study Materials page
        </Typography>
      </Box>
      <Box sx={{ mt: 3, mb: 1 }}>
        <Typography variant="h6" sx={{ fontSize: "16px" }} gutterBottom>
          Printer selection
        </Typography>

        <Box sx={{ maxWidth: "300px", mt: 2 }}>
          <FormControl fullWidth size="small">
            <FormLabel
              sx={{
                fontSize: "14px",
                color: "black",
                mb: 1,
                fontWeight: "400",
              }}
            >
              Select Printer{" "}
              <span style={{ color: "red", fontSize: "20px" }}>*</span>
            </FormLabel>

            <Select
              value={selectedPrinter}
              onChange={(e) => {
                setSelectedPrinter(e.target.value);
                setSelectedPrinterError(false);
              }}
              size="small"
              error={selectedPrinterError}
              displayEmpty
            >
              <MenuItem value="">
                <em>Select Printer</em>
              </MenuItem>

              {PrinterList?.data?.map((printer: any) => (
                <MenuItem key={printer.printer_id} value={printer.printer_id}>
                  {printer.printer_details?.name} (
                  {printer.printer_details?.model})
                </MenuItem>
              ))}
            </Select>

            {selectedPrinterError && (
              <Typography variant="caption" color="error" fontSize={"12px"}>
                Please select a printer
              </Typography>
            )}
          </FormControl>
        </Box>
      </Box>

      {/* Printing Settings */}
      <Box sx={{ mt: 1, mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "30px",
          }}
        >
          <Typography variant="h6" sx={{ fontSize: "16px" }} gutterBottom>
            Printing Format Details
          </Typography>

          <FormControlLabel
            sx={{ fontSize: "12px", fontWeight: "500" }}
            label="Edit printer options"
            control={
              <Switch
                checked={editPrinterOptions}
                disabled={!selectedPrinter}
                onChange={(e) => setEditPrinterOptions(e.target.checked)}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#009DDF", // thumb color when checked
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#009DDF", // track color when checked
                    opacity: 1,
                  },
                  "& .MuiSwitch-track": {
                    backgroundColor: "#bdbdbd", // track color when unchecked
                    opacity: 1,
                  },
                  "& .MuiSwitch-thumb": {
                    backgroundColor: "#757575", // thumb color when unchecked
                  },
                }}
              />
            }
          />
        </Box>

        <Box
          sx={{
            opacity: editPrinterOptions ? 1 : 0.5,
            pointerEvents: editPrinterOptions ? "auto" : "none",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(auto-fit, minmax(165px, 1fr))",
              },
              gap: { xs: "20px", md: "30px 40px" },
              width: "100%",
              maxWidth: "1060px",
              mt: 2,
            }}
          >
            {/* Paper Size */}
            <FormControl fullWidth size="small">
              <FormLabel sx={{ fontSize: "14px", color: "black", mb: 1 }}>
                Paper Size
              </FormLabel>
              <Select
                value={printForm.paperSize}
                onChange={(e) => handlePrintChange("paperSize", e.target.value)}
                size="small"
              >
                {optionsByCategory["CAT001"]?.map((opt: any) => (
                  <MenuItem key={opt.po_id} value={opt.po_id}>
                    {opt.printer_option_value}
                  </MenuItem>
                ))}
              </Select>

              {formErrors.paperSize && (
                <Typography variant="caption" color="error">
                  Required
                </Typography>
              )}
            </FormControl>

            {/* Side */}
            <FormControl fullWidth size="small">
              <FormLabel sx={{ fontSize: "14px", color: "black", mb: 1 }}>
                Side
              </FormLabel>
              <Select
                value={printForm.side}
                onChange={(e) => handlePrintChange("side", e.target.value)}
                size="small"
              >
                {optionsByCategory["CAT002"]?.map((opt: any) => (
                  <MenuItem key={opt.po_id} value={opt.po_id}>
                    {opt.printer_option_value}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.paperSize && (
                <Typography variant="caption" color="error">
                  Required
                </Typography>
              )}
            </FormControl>

            {/* Black & White / Color */}
            <FormControl fullWidth size="small">
              <FormLabel sx={{ fontSize: "14px", color: "black", mb: 1 }}>
                Black and White/Color
              </FormLabel>
              <Select
                value={printForm.color}
                onChange={(e) => handlePrintChange("color", e.target.value)}
                size="small"
              >
                {optionsByCategory["CAT007"]?.map((opt: any) => (
                  <MenuItem key={opt.po_id} value={opt.po_id}>
                    {opt.printer_option_value}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.paperSize && (
                <Typography variant="caption" color="error">
                  Required
                </Typography>
              )}
            </FormControl>

            <FormControl component="fieldset" size="small">
              <FormLabel sx={{ fontSize: "14px", color: "black", mb: 1 }}>
                Page Orientation
              </FormLabel>
              <RadioGroup
                row
                value={printForm.orientation}
                onChange={(e) =>
                  handlePrintChange("orientation", e.target.value)
                }
              >
                {optionsByCategory["CAT003"]?.map((opt: any) => (
                  <FormControlLabel
                    key={opt.po_id}
                    value={opt.po_id} // use the option value
                    control={<Radio size="small" />}
                    label={opt.printer_option_value}
                  />
                ))}
              </RadioGroup>
              {formErrors.paperSize && (
                <Typography variant="caption" color="error">
                  Required
                </Typography>
              )}
            </FormControl>

            {/* Scale */}
            <FormControl fullWidth size="small">
              <FormLabel sx={{ fontSize: "14px", color: "black", mb: 1 }}>
                Scale
              </FormLabel>
              <Select
                value={printForm.scale}
                onChange={(e) => handlePrintChange("scale", e.target.value)}
                size="small"
              >
                {optionsByCategory["CAT004"]?.map((opt: any) => (
                  <MenuItem key={opt.po_id} value={opt.po_id}>
                    {opt.printer_option_value}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.paperSize && (
                <Typography variant="caption" color="error">
                  Required
                </Typography>
              )}
            </FormControl>

            {/* Stapler Printing */}
            <FormControl component="fieldset" size="small">
              <FormLabel sx={{ fontSize: "14px", color: "black", mb: 1 }}>
                Stapler Printing
              </FormLabel>
              <RadioGroup
                row
                value={printForm.stapler}
                sx={{ color: "green" }}
                onChange={(e) => handlePrintChange("stapler", e.target.value)}
              >
                <FormControlLabel
                  value="YES"
                  control={<Radio size="small" />}
                  label="Yes"
                />
                <FormControlLabel
                  value="NO"
                  control={<Radio size="small" />}
                  label="No"
                />
              </RadioGroup>
              {formErrors.paperSize && (
                <Typography variant="caption" color="error">
                  Required
                </Typography>
              )}
            </FormControl>

            <FormControl
              fullWidth
              size="small"
              disabled={printForm.stapler !== "YES"}
            >
              <FormLabel sx={{ fontSize: "14px", color: "black", mb: 1 }}>
                Staple Position
              </FormLabel>
              <Select
                value={printForm.staplePosition}
                onChange={(e) =>
                  handlePrintChange("staplePosition", e.target.value || "")
                }
                size="small"
              >
                {optionsByCategory["CAT006"]?.map((opt: any) => (
                  <MenuItem key={opt.po_id} value={opt.po_id}>
                    {opt.printer_option_value}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.paperSize && (
                <Typography variant="caption" color="error">
                  Required
                </Typography>
              )}
            </FormControl>

            <FormControl fullWidth size="small">
              <FormLabel sx={{ fontSize: "14px", color: "black", mb: 1 }}>
                Page per sheet
              </FormLabel>
              <Select
                value={printForm.pages_per_sheet}
                onChange={(e) =>
                  handlePrintChange("pages_per_sheet", e.target.value || "")
                }
                size="small"
              >
                {optionsByCategory["CAT008"]?.map((opt: any) => (
                  <MenuItem key={opt.po_id} value={opt.po_id}>
                    {opt.printer_option_value}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.paperSize && (
                <Typography variant="caption" color="error">
                  Required
                </Typography>
              )}
            </FormControl>
          </Box>
        </Box>
      </Box>

      {/* Remark */}
      <Box>
        <Typography variant="subtitle1" sx={{ fontSize: "14px" }} gutterBottom>
          Remark <span style={{ color: "red", fontSize: "20px" }}>*</span>
        </Typography>

        <TextField
          fullWidth
          multiline
          minRows={3}
          placeholder="Enter your remark here..."
          value={printForm.remark}
          onChange={(e) => handlePrintChange("remark", e.target.value)}
          // error={!!formErrors.remark}
          // helperText={formErrors.remark ? "Remark is required" : ""}
          sx={{
            maxWidth: { xs: "100%", sm: "600px" },
            backgroundColor: "#f9f9f9",
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
            },
            "& .MuiFormHelperText-root": {
              fontSize: "12px",
            },
          }}
        />
      </Box>

      {/* Action Buttons */}
      <Box
        sx={{
          mt: 2,
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
        }}
      >
        <Button
          variant="outlined"
          sx={{
            color: "#0a0a0aff",
            borderColor: "none",
            fontSize: "14px",
            textTransform: "none",
            borderRadius: "20px",
          }}
          onClick={() => navigate("/study_materials")}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          // disabled={!isFormValid()}
          sx={{
            fontSize: "14px",
            color: "#ffffff",
            bgcolor: "#009DFF",
            borderRadius: "20px",
            textTransform: "none",
            "&:hover": {
              opacity: 0.8,
              bgcolor: "#009DFF",
            },
          }}
          onClick={handleSubmit}
        >
          {submitting ? <CircularProgress size={22} color="inherit" /> : "Send Request"}

        </Button>
      </Box>

      <ConfirmDialog
        open={openDialog}
        message={dialogMessage}
        onClose={() => setOpenDialog(false)}
        onConfirm={() => {
          setOpenDialog(false);
          navigate("/my_requests");
        }}
      />
    </Box>
  );
};

export default CartPage;
